import * as React from 'react';
import moment from 'moment';
import 'moment-timezone';
import { split } from 'lodash-es';

import { padString } from './Format';
import type { Moment } from '../../models';
import UserManager from './UserManager';

export type TimezoneOption = {
  value: string;
  offset: number;
  filterString: string;
  label: string;
};

function computeTimezoneOffset(tzName: string, d: Moment | null = null): number {
  let date = d;
  if (!date) {
    date = moment();
  }
  const zone = moment.tz.zone(tzName);
  // @ts-expect-error - TS2531 - Object is possibly 'null'.
  const offset = zone.utcOffset(date);
  return offset;
}

function formatRawTimezoneOffset(offset: number): string {
  // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
  const offsetHour = padString(Math.floor(Math.abs(offset) / 60), 2);
  // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
  const offsetMins = padString(Math.abs(offset) % 60, 2);

  // Straight from the moment docs:
  // POSIX compatibility requires that the offsets are inverted. Therefore,
  // Etc/GMT-X will have an offset of +X and Etc/GMT+X will have an offset of
  // -X. This is a result of IANA's Time Zone Database and not an arbitrary
  // choice by Moment.js. Thus, using locality based identifiers is preferred
  // over fixed-offset identifiers.
  //
  // See https://momentjs.com/timezone/docs/#/zone-object/offset/
  if (offset < 0) {
    return `+${offsetHour}:${offsetMins}`;
  }
  return `-${offsetHour}:${offsetMins}`;
}

function formatTimezoneOffset(offset: number, prefix: string = 'GMT'): string {
  return `${prefix}${formatRawTimezoneOffset(offset)}`;
}

// AVAILABLE_TIMEZONES are taken directly from Apple's list of timezones for a
// mac. This list was generated using `sudo systemsetup -listtimezones`
const AVAILABLE_TIMEZONES = [
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Addis_Ababa',
  'Africa/Algiers',
  'Africa/Asmara',
  'Africa/Bamako',
  'Africa/Bangui',
  'Africa/Banjul',
  'Africa/Bissau',
  'Africa/Blantyre',
  'Africa/Brazzaville',
  'Africa/Bujumbura',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Ceuta',
  'Africa/Conakry',
  'Africa/Dakar',
  'Africa/Dar_es_Salaam',
  'Africa/Djibouti',
  'Africa/Douala',
  'Africa/El_Aaiun',
  'Africa/Freetown',
  'Africa/Gaborone',
  'Africa/Harare',
  'Africa/Johannesburg',
  'Africa/Juba',
  'Africa/Kampala',
  'Africa/Khartoum',
  'Africa/Kigali',
  'Africa/Kinshasa',
  'Africa/Lagos',
  'Africa/Libreville',
  'Africa/Lome',
  'Africa/Luanda',
  'Africa/Lubumbashi',
  'Africa/Lusaka',
  'Africa/Malabo',
  'Africa/Maputo',
  'Africa/Maseru',
  'Africa/Mbabane',
  'Africa/Mogadishu',
  'Africa/Monrovia',
  'Africa/Nairobi',
  'Africa/Ndjamena',
  'Africa/Niamey',
  'Africa/Nouakchott',
  'Africa/Ouagadougou',
  'Africa/Porto-Novo',
  'Africa/Sao_Tome',
  'Africa/Tripoli',
  'Africa/Tunis',
  'Africa/Windhoek',
  'America/Adak',
  'America/Anchorage',
  'America/Anguilla',
  'America/Antigua',
  'America/Araguaina',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/La_Rioja',
  'America/Argentina/Mendoza',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Salta',
  'America/Argentina/San_Juan',
  'America/Argentina/San_Luis',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'America/Aruba',
  'America/Asuncion',
  'America/Atikokan',
  'America/Bahia',
  'America/Bahia_Banderas',
  'America/Barbados',
  'America/Belem',
  'America/Belize',
  'America/Blanc-Sablon',
  'America/Boa_Vista',
  'America/Bogota',
  'America/Boise',
  'America/Cambridge_Bay',
  'America/Campo_Grande',
  'America/Cancun',
  'America/Caracas',
  'America/Cayenne',
  'America/Cayman',
  'America/Chicago',
  'America/Chihuahua',
  'America/Costa_Rica',
  'America/Creston',
  'America/Cuiaba',
  'America/Curacao',
  'America/Danmarkshavn',
  'America/Dawson',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Detroit',
  'America/Dominica',
  'America/Edmonton',
  'America/Eirunepe',
  'America/El_Salvador',
  'America/Fort_Nelson',
  'America/Fortaleza',
  'America/Glace_Bay',
  'America/Godthab',
  'America/Goose_Bay',
  'America/Grand_Turk',
  'America/Grenada',
  'America/Guadeloupe',
  'America/Guatemala',
  'America/Guayaquil',
  'America/Guyana',
  'America/Halifax',
  'America/Havana',
  'America/Hermosillo',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Inuvik',
  'America/Iqaluit',
  'America/Jamaica',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Kralendijk',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Lower_Princes',
  'America/Maceio',
  'America/Managua',
  'America/Manaus',
  'America/Marigot',
  'America/Martinique',
  'America/Matamoros',
  'America/Mazatlan',
  'America/Menominee',
  'America/Merida',
  'America/Metlakatla',
  'America/Mexico_City',
  'America/Miquelon',
  'America/Moncton',
  'America/Monterrey',
  'America/Montevideo',
  'America/Montreal',
  'America/Montserrat',
  'America/Nassau',
  'America/New_York',
  'America/Nipigon',
  'America/Nome',
  'America/Noronha',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Ojinaga',
  'America/Panama',
  'America/Pangnirtung',
  'America/Paramaribo',
  'America/Phoenix',
  'America/Port-au-Prince',
  'America/Port_of_Spain',
  'America/Porto_Velho',
  'America/Puerto_Rico',
  'America/Punta_Arenas',
  'America/Rainy_River',
  'America/Rankin_Inlet',
  'America/Recife',
  'America/Regina',
  'America/Resolute',
  'America/Rio_Branco',
  'America/Santa_Isabel',
  'America/Santarem',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/Scoresbysund',
  'America/Shiprock',
  'America/Sitka',
  'America/St_Barthelemy',
  'America/St_Johns',
  'America/St_Kitts',
  'America/St_Lucia',
  'America/St_Thomas',
  'America/St_Vincent',
  'America/Swift_Current',
  'America/Tegucigalpa',
  'America/Thule',
  'America/Thunder_Bay',
  'America/Tijuana',
  'America/Toronto',
  'America/Tortola',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Winnipeg',
  'America/Yakutat',
  'America/Yellowknife',
  'Antarctica/Casey',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Antarctica/Macquarie',
  'Antarctica/Mawson',
  'Antarctica/McMurdo',
  'Antarctica/Palmer',
  'Antarctica/Rothera',
  'Antarctica/South_Pole',
  'Antarctica/Syowa',
  'Antarctica/Troll',
  'Antarctica/Vostok',
  'Arctic/Longyearbyen',
  'Asia/Aden',
  'Asia/Almaty',
  'Asia/Amman',
  'Asia/Anadyr',
  'Asia/Aqtau',
  'Asia/Aqtobe',
  'Asia/Ashgabat',
  'Asia/Atyrau',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Barnaul',
  'Asia/Beirut',
  'Asia/Bishkek',
  'Asia/Brunei',
  'Asia/Calcutta',
  'Asia/Chita',
  'Asia/Choibalsan',
  'Asia/Chongqing',
  'Asia/Colombo',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dili',
  'Asia/Dubai',
  'Asia/Dushanbe',
  'Asia/Famagusta',
  'Asia/Gaza',
  'Asia/Harbin',
  'Asia/Hebron',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Hovd',
  'Asia/Irkutsk',
  'Asia/Jakarta',
  'Asia/Jayapura',
  'Asia/Jerusalem',
  'Asia/Kabul',
  'Asia/Kamchatka',
  'Asia/Karachi',
  'Asia/Kashgar',
  'Asia/Kathmandu',
  'Asia/Katmandu',
  'Asia/Khandyga',
  'Asia/Krasnoyarsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
  'Asia/Kuwait',
  'Asia/Macau',
  'Asia/Magadan',
  'Asia/Makassar',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Nicosia',
  'Asia/Novokuznetsk',
  'Asia/Novosibirsk',
  'Asia/Omsk',
  'Asia/Oral',
  'Asia/Phnom_Penh',
  'Asia/Pontianak',
  'Asia/Pyongyang',
  'Asia/Qatar',
  'Asia/Qyzylorda',
  'Asia/Rangoon',
  'Asia/Riyadh',
  'Asia/Sakhalin',
  'Asia/Samarkand',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Srednekolymsk',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Thimphu',
  'Asia/Tokyo',
  'Asia/Tomsk',
  'Asia/Ulaanbaatar',
  'Asia/Urumqi',
  'Asia/Ust-Nera',
  'Asia/Vientiane',
  'Asia/Vladivostok',
  'Asia/Yakutsk',
  'Asia/Yangon',
  'Asia/Yekaterinburg',
  'Asia/Yerevan',
  'Atlantic/Azores',
  'Atlantic/Bermuda',
  'Atlantic/Canary',
  'Atlantic/Cape_Verde',
  'Atlantic/Faroe',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
  'Atlantic/South_Georgia',
  'Atlantic/St_Helena',
  'Atlantic/Stanley',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Broken_Hill',
  'Australia/Currie',
  'Australia/Darwin',
  'Australia/Eucla',
  'Australia/Hobart',
  'Australia/Lindeman',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Astrakhan',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Bratislava',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Busingen',
  'Europe/Chisinau',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Gibraltar',
  'Europe/Guernsey',
  'Europe/Helsinki',
  'Europe/Isle_of_Man',
  'Europe/Istanbul',
  'Europe/Jersey',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Kirov',
  'Europe/Lisbon',
  'Europe/Ljubljana',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Mariehamn',
  'Europe/Minsk',
  'Europe/Monaco',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Podgorica',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Samara',
  'Europe/San_Marino',
  'Europe/Sarajevo',
  'Europe/Saratov',
  'Europe/Simferopol',
  'Europe/Skopje',
  'Europe/Sofia',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Tirane',
  'Europe/Ulyanovsk',
  'Europe/Uzhgorod',
  'Europe/Vaduz',
  'Europe/Vatican',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Volgograd',
  'Europe/Warsaw',
  'Europe/Zagreb',
  'Europe/Zaporozhye',
  'Europe/Zurich',
  'GMT',
  'Indian/Antananarivo',
  'Indian/Chagos',
  'Indian/Christmas',
  'Indian/Cocos',
  'Indian/Comoro',
  'Indian/Kerguelen',
  'Indian/Mahe',
  'Indian/Maldives',
  'Indian/Mauritius',
  'Indian/Mayotte',
  'Indian/Reunion',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Bougainville',
  'Pacific/Chatham',
  'Pacific/Chuuk',
  'Pacific/Easter',
  'Pacific/Efate',
  'Pacific/Enderbury',
  'Pacific/Fakaofo',
  'Pacific/Fiji',
  'Pacific/Funafuti',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Pacific/Guadalcanal',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Johnston',
  'Pacific/Kiritimati',
  'Pacific/Kosrae',
  'Pacific/Kwajalein',
  'Pacific/Majuro',
  'Pacific/Marquesas',
  'Pacific/Midway',
  'Pacific/Nauru',
  'Pacific/Niue',
  'Pacific/Norfolk',
  'Pacific/Noumea',
  'Pacific/Pago_Pago',
  'Pacific/Palau',
  'Pacific/Pitcairn',
  'Pacific/Pohnpei',
  'Pacific/Ponape',
  'Pacific/Port_Moresby',
  'Pacific/Rarotonga',
  'Pacific/Saipan',
  'Pacific/Tahiti',
  'Pacific/Tarawa',
  'Pacific/Tongatapu',
  'Pacific/Truk',
  'Pacific/Wake',
  'Pacific/Wallis',
  'UTC',
];

export const cleanTimezoneName = (tz: string) => {
  return tz.replace(/[_-]/g, ' ');
};

export const getTimezoneCity = (tz: string) => {
  const cleanedTimezoneName = cleanTimezoneName(tz);
  const splitTimezone = split(cleanedTimezoneName, '/');
  if (splitTimezone.length > 1) {
    return splitTimezone[1];
  }
  return tz;
};

function timezoneOptions(d: Moment | null = null): Array<TimezoneOption> {
  let date = d;
  if (!date) {
    date = moment();
  }
  const opts = AVAILABLE_TIMEZONES.map((tzName) => {
    const offset = computeTimezoneOffset(tzName, date);
    const offsetLabel = formatTimezoneOffset(offset);
    const cleanedName = cleanTimezoneName(tzName);
    const label = `(${offsetLabel}) ${cleanedName}`;
    return {
      value: tzName,
      filterString: `${formatRawTimezoneOffset(offset)} ${cleanedName}`,
      offset,
      label,
    };
  });
  opts.sort((a, b) => a.offset - b.offset);
  return opts;
}

// Takes a time with timezone, strips the timezone and returns a time with a
// new timezone and an adjusted utc offset to keep the time the same but not
// necessarily the unix timestamp.
// Ex: for args (midnight PDT, 'Europe/London')
// will return  midnight Europe/London
function convertToTimezone(t: Moment, timezone: string): Moment {
  return moment.tz(t.format('YYYY-MM-DD HH:mm:ss'), timezone);
}

// This function returns a timestamp with the reverse timezone offset of the user applied
// This inverted offset is used to get the same date and time in the users timezone when using moment.unix(timestamp)
// Example:
//    We have timestamp -> 2022-01-01 00:00:00 UTC
//    We want timestamp -> 2022-01-01 00:00:00 America/Los_Angeles (-7 hour offset)
//    We get  timestamp -> 2022-01-01 07:00:00 UTC (timestampWithInvertedOffset)
//      so moment.unix(timestampWithInvertedOffset) gives 2022-01-01 00:00:00 America/Los_Angeles (-7 hour offset)
function AddInvertedTimezoneOffset(utcTimestamp: number): number {
  const date = moment.unix(utcTimestamp);
  const timezone = UserManager.timezone();
  const offsetMinutes = moment.tz(timezone).utcOffset();
  return date.subtract(offsetMinutes, 'minutes').unix();
}

type TimezoneLabelProps = {
  timezone: string;
  style?: React.CSSProperties;
  time: Moment | null | undefined;
};

function TimezoneLabel(props: TimezoneLabelProps): React.ReactElement {
  let timezoneLabel;
  if (props.time) {
    timezoneLabel = props.time.clone().tz(props.timezone).format('z');
  } else {
    timezoneLabel = moment.tz(props.timezone).format('z');
  }
  return (
    <span className="timezone-label" style={props.style}>
      {timezoneLabel}
    </span>
  );
}

type OffsetLabelProps = {
  timezone: string;
  style?: React.CSSProperties;
  time: Moment | null | undefined;
};

function OffsetLabel(props: OffsetLabelProps): React.ReactElement {
  const offset = computeTimezoneOffset(props.timezone, props.time);
  const offsetLabel = formatTimezoneOffset(offset);
  return (
    <span className="offset-label" style={props.style}>
      ({offsetLabel})
    </span>
  );
}

type FormattedTimezoneProps = {
  timezone: string;
  time: Moment | null | undefined;
};

function FormattedTimezone(props: FormattedTimezoneProps) {
  const { timezone, time } = props;
  return (
    <>
      <OffsetLabel timezone={timezone} time={time} />
      <TimezoneLabel
        style={{
          marginLeft: '5px',
        }}
        timezone={timezone}
        time={time}
      />
    </>
  );
}

export {
  computeTimezoneOffset,
  formatRawTimezoneOffset,
  formatTimezoneOffset,
  timezoneOptions,
  convertToTimezone,
  FormattedTimezone,
  TimezoneLabel,
  AddInvertedTimezoneOffset,
};
